import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Pill, Users, Lock, ChevronRight, ChevronLeft, AlertCircle, MailWarning } from 'lucide-react';
import { useLogin, useTeamLogin, useResendVerification } from '@/api/auth';
import { ApiError } from '@/api/client';
import { clerkSignOut, clerkEmail } from '@/lib/clerkSso';

export default function LoginPage() {
  const navigate = useNavigate();
  const [showPinPad, setShowPinPad] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pharmacyId, setPharmacyId] = useState('');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);

  const loginMut = useLogin();
  const teamLoginMut = useTeamLogin();
  const resendMut = useResendVerification();

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    resendMut.reset();
    try {
      const { user } = await loginMut.mutateAsync({ email, password });
      if (user.role === 'TITULAIRE') navigate('/dashboard');
      else if (user.role === 'SALARIE') navigate('/my/week');
    } catch {
      // Erreur affichée via loginMut.error
    }
  }

  /**
   * Adresse pas encore confirmée. Le serveur le dit par un code dédié plutôt
   * que par le texte du message : un refus sans issue laisserait la personne
   * bloquée, alors qu'il lui manque juste un mail.
   */
  const err = loginMut.error;
  const notVerified =
    err instanceof ApiError &&
    err.status === 403 &&
    (err.body as { code?: string } | null)?.code === 'EMAIL_NOT_VERIFIED';

  /**
   * Session Clerk ouverte mais aucun compte Officina derrière : la personne
   * s'est authentifiée, et pourtant rien ne s'ouvre. Sans ce message, elle
   * verrait une page vide sans comprendre.
   *
   * Ne se produit que sur `rh.pharmagestion.fr` — un titulaire dont l'adresse
   * PharmaGestion ne correspond à aucun compte Officina.
   */
  const [orphanEmail, setOrphanEmail] = useState<string | null>(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const email = clerkEmail();
      // On est TOUJOURS sur /login alors qu'une session Clerk existe : le
      // rapprochement a échoué (sinon RootRedirect nous aurait déjà sortis).
      if (email) setOrphanEmail(email);
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  function handlePinPress(digit: string) {
    if (pin.length >= 4) return;
    setPinError(false);
    const newPin = pin + digit;
    setPin(newPin);
    if (newPin.length === 4 && pharmacyId) {
      teamLoginMut
        .mutateAsync({ pharmacyId, pin: newPin })
        .then(() => navigate('/team'))
        .catch(() => {
          setPinError(true);
          setTimeout(() => {
            setPin('');
            setPinError(false);
          }, 600);
        });
    }
  }

  return (
    <div className="w-full h-screen flex">
      {/* Panneau gauche — visuel */}
      <div
        className="hidden lg:flex w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ backgroundColor: '#0f1f35' }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, #ff6b57 0%, transparent 40%), radial-gradient(circle at 80% 70%, #1e3a5f 0%, transparent 50%)',
          }}
        />
        <div className="relative z-10 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center">
            <Pill size={19} color="#fff" strokeWidth={2.25} />
          </div>
          <div>
            <div className="text-[17px] font-semibold text-white">Officina</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-white/50">RH Pharmacie</div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <div className="text-[11px] uppercase tracking-[0.15em] text-accent mb-4 font-medium">
            Le logiciel RH des pharmacies
          </div>
          <h1 className="text-white text-[36px] leading-[1.1] font-semibold tracking-tight">
            Votre planning, vos compétences, votre fiche navette. Au même endroit.
          </h1>
          <p className="text-white/60 mt-5 text-[14px] leading-relaxed">
            Conçu pour les pharmaciens titulaires. Compétences, cycles de roulement, vaccination, TROD, fiche navette PDF — tout est prévu.
          </p>
        </div>

        <div className="relative z-10 flex gap-8 text-[11px] text-white/40">
          <span>✓ RGPD · données en France</span>
          <span>✓ Pensé avec des titulaires</span>
        </div>
      </div>

      {/* Panneau droit */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        {!showPinPad ? (
          <div className="w-full max-w-[380px]">
            <div className="mb-8">
              <h2 className="text-[24px] font-semibold tracking-tight text-slate-900">Se connecter</h2>
              <p className="text-[13px] text-slate-500 mt-1">Accès individuel titulaire ou salarié</p>
            </div>

            {orphanEmail && (
              <div className="mb-5 bg-amber-50 border border-amber-300 rounded-lg p-3 text-[11.5px] text-amber-900">
                <div className="flex items-start gap-2">
                  <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                  <span>
                    Vous êtes connecté en tant que <strong>{orphanEmail}</strong>, mais aucun compte
                    Officina ne porte cette adresse. Connectez-vous avec l'adresse que votre
                    pharmacie utilise dans Officina.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    void clerkSignOut().then(() => setOrphanEmail(null));
                  }}
                  className="mt-2 ml-[21px] font-medium underline hover:no-underline"
                >
                  Changer de compte
                </button>
              </div>
            )}

            {/* Pas de bouton PharmaGestion ici.
                `pharmarh.fr` est le portail des SALARIÉS, et un salarié n'a pas
                de compte PharmaGestion : il se connecte par mot de passe. Les
                titulaires, eux, passent par rh.pharmagestion.fr. Proposer les
                deux portes au même endroit n'aurait fait qu'égarer tout le
                monde — et aurait exigé de déclarer pharmarh.fr en domaine
                satellite chez Clerk (plan payant + DNS). */}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  required
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-medium text-slate-700">Mot de passe</label>
                  <Link to="/forgot-password" className="text-[11px] text-primary hover:underline">
                    Oublié ?
                  </Link>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  required
                />
              </div>

              {notVerified ? (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-[11.5px] text-amber-900">
                  <div className="flex items-start gap-2">
                    <MailWarning size={13} className="mt-0.5 flex-shrink-0" />
                    <span>
                      Votre adresse n'est pas encore confirmée. Ouvrez le mail que nous vous avons
                      envoyé pour accéder à votre espace.
                    </span>
                  </div>
                  {resendMut.isSuccess ? (
                    <div className="mt-2 pl-[21px] text-emerald-700">
                      Nouveau lien envoyé. Pensez à regarder dans les indésirables.
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={resendMut.isPending}
                      onClick={() => resendMut.mutate({ email })}
                      className="mt-2 ml-[21px] font-medium underline hover:no-underline disabled:opacity-50"
                    >
                      {resendMut.isPending ? 'Envoi…' : 'Renvoyer le mail de confirmation'}
                    </button>
                  )}
                </div>
              ) : (
                loginMut.error && (
                  <div className="bg-danger-bg border border-danger/30 rounded-lg p-2.5 flex items-start gap-2 text-[11.5px] text-danger">
                    <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                    <span>{(loginMut.error as Error).message || 'Identifiants invalides'}</span>
                  </div>
                )
              )}

              <button
                type="submit"
                disabled={loginMut.isPending}
                className="w-full bg-primary hover:bg-primary-hover text-white font-medium text-[13px] py-2.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {loginMut.isPending ? 'Connexion…' : 'Se connecter'}
              </button>
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-medium">ou</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <button
              onClick={() => setShowPinPad(true)}
              className="w-full group bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-accent/40 rounded-xl p-4 text-left transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent text-white flex items-center justify-center flex-shrink-0">
                  <Users size={16} strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-slate-900">Accès Équipe</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Pour les postes partagés (comptoir, back office). Code PIN à 4 chiffres.
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-400 group-hover:text-accent mt-2" />
              </div>
            </button>

            {/* « Créer une pharmacie » retiré le 2026-09-04 : plus aucune
                inscription libre. Les comptes viennent de PharmaGestion, seule
                source d'identité de la suite. Une seconde porte créerait des
                comptes que le hub ne connaîtrait pas. */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="text-[11px] text-slate-500 text-center">
                Vous êtes titulaire ?{' '}
                <a
                  href="https://pharmagestion.fr"
                  className="text-primary font-medium hover:underline"
                >
                  Connectez-vous sur PharmaGestion
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-[380px]">
            <button
              onClick={() => {
                setShowPinPad(false);
                setPin('');
              }}
              className="text-[11px] text-slate-500 hover:text-slate-900 mb-6 flex items-center gap-1"
            >
              <ChevronLeft size={12} /> Retour
            </button>

            <div className="mb-6">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-lg bg-accent text-white flex items-center justify-center">
                  <Users size={16} strokeWidth={2} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-accent font-semibold">Accès Équipe</div>
                  <h2 className="text-[22px] font-semibold tracking-tight text-slate-900 leading-tight">
                    Code PIN de la pharmacie
                  </h2>
                </div>
              </div>
              <p className="text-[12px] text-slate-500">
                Saisissez d'abord l'identifiant de la pharmacie, puis le code PIN.
              </p>
            </div>

            <div className="mb-4">
              <label className="label">ID Pharmacie</label>
              <input
                value={pharmacyId}
                onChange={(e) => setPharmacyId(e.target.value)}
                placeholder="Fourni par le titulaire"
                className="input"
              />
            </div>

            <div className="flex justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`w-14 h-16 rounded-xl border-2 flex items-center justify-center text-[24px] font-semibold tabular-nums transition-all ${
                    pinError
                      ? 'border-danger bg-danger-bg text-danger animate-pulse'
                      : pin.length > i
                      ? 'border-primary bg-primary-bg text-primary'
                      : 'border-slate-200 bg-slate-50 text-slate-300'
                  }`}
                >
                  {pin.length > i ? '●' : ''}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-2 max-w-[280px] mx-auto">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button
                  key={d}
                  onClick={() => handlePinPress(d)}
                  disabled={!pharmacyId}
                  className="aspect-square bg-white border border-slate-200 hover:border-primary hover:bg-primary-bg/40 disabled:opacity-30 disabled:cursor-not-allowed rounded-xl text-[22px] font-semibold text-slate-900 transition-all tabular-nums"
                >
                  {d}
                </button>
              ))}
              <div />
              <button
                onClick={() => handlePinPress('0')}
                disabled={!pharmacyId}
                className="aspect-square bg-white border border-slate-200 hover:border-primary hover:bg-primary-bg/40 disabled:opacity-30 rounded-xl text-[22px] font-semibold text-slate-900 transition-all tabular-nums"
              >
                0
              </button>
              <button
                onClick={() => setPin((p) => p.slice(0, -1))}
                className="aspect-square bg-white border border-slate-200 hover:border-slate-400 rounded-xl flex items-center justify-center text-slate-500 transition-all"
              >
                <ChevronLeft size={20} />
              </button>
            </div>

            <div className="mt-6 bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-2.5">
              <Lock size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
              <div className="text-[10.5px] text-slate-600 leading-relaxed">
                Le mode équipe donne accès uniquement à l'organisation du jour et à la prise de rendez-vous.{' '}
                <strong>Aucune donnée personnelle n'est accessible</strong>.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
